import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Link as MuiLink, 
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Facebook, 
  Instagram,
  Language,
  Business,
  Email,
  Phone,
  LocationOn,
  Info,
  Restaurant,
  ContactMail,
  ShoppingBag,
  Cake,
  People,
  Close

} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { keyframes } from '@emotion/react';

// Custom keyframe animation
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const Footer = () => {
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const [openDevDialog, setOpenDevDialog] = useState(false);

  const handleDevDialogOpen = () => {
    setOpenDevDialog(true);
  };

  const handleDevDialogClose = () => {
    setOpenDevDialog(false);
  };

  const socialIcons = [
    { icon: <Facebook fontSize="medium" />, color: '#4267B2', name: 'Facebook' },
    { icon: <Instagram fontSize="medium" />, color: '#E1306C', name: 'Instagram' }
  ];

  const quickLinks = [
    { icon: <Info />, text: 'About Us', href: '/about' },
    { icon: <ShoppingBag />, text: 'Shop', href: '/shop' },
    { icon: <Cake />, text: 'Products', href: '/products' },
    { icon: <People />, text: 'Customers', href: '/customers' },
    { icon: <Restaurant />, text: 'Our Story', href: '/about' },
    { icon: <ContactMail />, text: 'Contact Us', href: '/contact' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <Box 
      component="footer" 
      sx={{ 
        backgroundColor: '#121212', 
        color: 'white', 
        py: 6,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated background elements */}
      <Box 
        component={motion.div}
        initial={{ x: -100, y: -100, rotate: 45 }}
        animate={{ 
          x: [null, 100, -50, 0],
          y: [null, 50, -30, 0],
          rotate: [45, 90, 135, 180],
          transition: { 
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }
        }}
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: 100,
          height: 100,
          border: '1px solid rgba(255, 165, 0, 0.1)',
          borderRadius: '50%',
          zIndex: 0
        }}
      />
      
      <Box 
        component={motion.div}
        initial={{ x: 100, y: 100, rotate: -45 }}
        animate={{ 
          x: [null, -100, 50, 0],
          y: [null, -50, 30, 0],
          rotate: [-45, -90, -135, -180],
          transition: { 
            duration: 25,
            repeat: Infinity,
            repeatType: "reverse"
          }
        }}
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: 150,
          height: 150,
          border: '1px solid rgba(255, 165, 0, 0.1)',
          borderRadius: '50%',
          zIndex: 0
        }}
      />

      <Container maxWidth="lg">
        <Grid 
          container 
          spacing={4}
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Company Info Column */}
          <Grid item xs={12} md={4} component={motion.div} variants={itemVariants}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
              <Box 
                component="span" 
                sx={{ 
                  color: '#FFA500',
                  display: 'inline-block',
                  animation: `${floatAnimation} 4s ease-in-out infinite`
                }}
              >
                SAI SWAGAT
              </Box>
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Traditional sweets and snacks crafted with love and authenticity.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              {socialIcons.map((social, index) => (
                <MuiLink 
                  key={index}
                  href={`https://www.${social.name.toLowerCase()}.com/saiswagat`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: hoveredIcon === index ? social.color : 'inherit',
                    transition: 'color 0.3s ease',
                    '&:hover': { 
                      color: social.color,
                      transform: 'scale(1.2)'
                    }
                  }}
                  onMouseEnter={() => setHoveredIcon(index)}
                  onMouseLeave={() => setHoveredIcon(null)}
                  component={motion.a}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {social.icon}
                </MuiLink>
              ))}
            </Box>
          </Grid>
          
          {/* Contact Column */}
          <Grid item xs={12} md={4} component={motion.div} variants={itemVariants}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>
              <Business sx={{ 
                mr: 1,
                animation: `${floatAnimation} 3s ease-in-out infinite`,
                color: '#FFA500'
              }} /> 
              Contact
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOn sx={{ 
                mr: 2, 
                color: 'rgba(255,255,255,0.7)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  color: '#FFA500'
                }
              }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Plot No-20/655, Bahilipada, Khata No-65/547<br />
                Harirajpur, Bhubaneswar, Khorda-752050<br />
                Odisha, India
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Phone sx={{ 
                mr: 2, 
                color: 'rgba(255,255,255,0.7)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  color: '#FFA500'
                }
              }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                +91 98401 10515
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Email sx={{ 
                mr: 2, 
                color: 'rgba(255,255,255,0.7)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  color: '#FFA500'
                }
              }} />
              <MuiLink 
                href="mailto:info@saiswagat.com" 
                sx={{ 
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': { 
                    color: 'white',
                    textDecoration: 'underline'
                  }
                }}
                component={motion.a}
                whileHover={{ x: 5 }}
              >
                info@saiswagat.com
              </MuiLink>
            </Box>
            <Box sx={{ mt: 3 }}>
              <MuiLink 
                href="https://www.saiswagat.com" 
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1.5,
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': { 
                    color: 'white',
                    textDecoration: 'underline'
                  }
                }}
                component={motion.a}
                whileHover={{ x: 5 }}
              >
                <Language sx={{ 
                  mr: 1,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'rotate(15deg)',
                    color: '#FFA500'
                  }
                }} />
                www.saiswagat.com
              </MuiLink>
            </Box>
          </Grid>
          
          {/* Quick Links Column */}
          <Grid item xs={12} md={4} component={motion.div} variants={itemVariants}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#FFA500' }}>
              Quick Links
            </Typography>
            {quickLinks.map((link, index) => (
              <MuiLink 
                key={index}
                href={link.href} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1.5,
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': { 
                    color: '#FFA500',
                    textDecoration: 'underline'
                  }
                }}
                component={motion.a}
                whileHover={{ x: 5 }}
              >
                <Box 
                  component="span"
                  sx={{ 
                    mr: 1,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.2)',
                      color: '#FFA500'
                    }
                  }}
                >
                  {link.icon}
                </Box>
                {link.text}
              </MuiLink>
            ))}
          </Grid>
        </Grid>
        
        <Divider sx={{ 
          my: 4, 
          backgroundColor: 'rgba(255,165,0,0.2)',
          borderBottomWidth: 1
        }} />
        
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 2,
            position: 'relative'
          }}
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {/* Developer credit - bottom left */}
          <Box 
            sx={{ 
              position: { xs: 'static', sm: 'absolute' },
              left: 0,
              bottom: -20,
              textAlign: 'left'
            }}
          >
            <Tooltip title="Click for developer info" arrow>
              <MuiLink
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDevDialogOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'rgba(255,255,255,0.3)',
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  bgcolor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'rgba(255,255,255,0.5)'
                  }
                }}
              >
                Developed by Aediax 
              </MuiLink>
            </Tooltip>
          </Box>

          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255,255,255,0.5)',
              textAlign: { xs: 'center', sm: 'left', left:5 }
            }}
            component={motion.div}
            whileHover={{ scale: 1.02 }}
          >
            Preserving traditional recipes since 1995
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255,255,255,0.5)',
              textAlign: { xs: 'center', sm: 'right' }
            }}
            component={motion.div}
            whileHover={{ scale: 1.02 }}
          >
            © {new Date().getFullYear()} <Box component="span" sx={{ color: '#FFA500' }}>SAI SWAGAT</Box> SWEET AND SNACKS PRIVATE LIMITED. All Rights Reserved.
          </Typography>
        </Box>
      </Container>

      {/* Developer Info Dialog */}
      <Dialog
        open={openDevDialog}
        onClose={handleDevDialogClose}
        maxWidth="xs"
        fullWidth
        component={motion.div}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', damping: 25 }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>Developer Information</Box>
          <IconButton 
            onClick={handleDevDialogClose}
            component={motion.button}
            whileHover={{ rotate: 90, scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This website was developed by Aediax Tech Private Limited.
          </DialogContentText>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Language sx={{ mr: 2, color: 'text.secondary' }} />
            <MuiLink 
              href="https://www.aediax.com" 
              target="_blank"
              sx={{ textDecoration: 'none' }}
              component={motion.a}
              whileHover={{ scale: 1.02 }}
            >
              www.aediax.com
            </MuiLink>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Email sx={{ mr: 2, color: 'text.secondary' }} />
            <MuiLink 
              href="mailto:info@aediax.com" 
              sx={{ textDecoration: 'none' }}
              component={motion.a}
              whileHover={{ scale: 1.02 }}
            >
              info@aediax.com
            </MuiLink>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDevDialogClose} 
            color="primary"
            component={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Footer;